import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, MapPin, ArrowRight, Loader2, Mail, Lock, Phone, ShieldCheck, BarChart3, Users, Zap, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { KeywordPicker } from "@/components/report/KeywordPicker";
import Logo from "@/components/Logo";
import LoadingScreen from "@/components/LoadingScreen";
import { useAuth } from "@/context/AuthContext";
import { funnelTrack, funnelStart } from "@/lib/funnel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Frontend cache for autocomplete results ── */
const autocompleteCache = {};

export default function AuditPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const debounceRef = useRef(null);

  const [step, setStep] = useState(1);
  const [businessQuery, setBusinessQuery] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [city, setCity] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [generating, setGenerating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [paymentRequired, setPaymentRequired] = useState(null);

  const handleInputChange = useCallback((value) => {
    setBusinessQuery(value);
    setSelectedBusiness(null);
    clearTimeout(debounceRef.current);
    if (value.length < 2) { setPredictions([]); setSearching(false); return; }

    const cacheKey = value.toLowerCase().trim();
    if (autocompleteCache[cacheKey]) {
      setPredictions(autocompleteCache[cacheKey]);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/autocomplete`, { input: value });
        const results = res.data?.predictions || [];
        autocompleteCache[cacheKey] = results;
        setPredictions(results);
      } catch { setPredictions([]); }
      setSearching(false);
    }, 200);
  }, []);

  // Pre-fill email and phone from auth context or sessionStorage
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
    const prefill = JSON.parse(sessionStorage.getItem("landing_prefill") || "{}");
    if (prefill.phoneNumber && !phoneNumber) setPhoneNumber(prefill.phoneNumber);
  }, [user]);

  const selectBusiness = async (pred) => {
    setPredictions([]);
    setBusinessQuery(pred.main_text || pred.description);
    // Auto-fill city from secondary_text (address)
    const addrText = pred.secondary_text || pred.description || "";
    const cityGuess = extractCityFromAddress(addrText);
    if (cityGuess) setCity(cityGuess);
    try {
      const res = await axios.post(`${API}/place-details`, { place_id: pred.place_id });
      const biz = res.data;
      const detailCity = biz.city || cityGuess || "";
      if (detailCity) setCity(detailCity);
      setSelectedBusiness({
        name: biz.name,
        address: biz.address,
        place_id: pred.place_id,
        types: biz.types,
        category: biz.category,
        city: detailCity,
      });
      funnelTrack("funnel.business_selected", {
        place_id: pred.place_id,
        business_name: biz.name,
        step: "1",
      });
    } catch { toast.error("Could not load business details."); }
  };

  function extractCityFromAddress(addr) {
    if (!addr) return "";
    const parts = addr.split(",").map(p => p.trim()).filter(Boolean);
    // Google Places format: "Street, City, State, Country"
    // Skip street (first part), skip state/country (last parts)
    // City is typically the 2nd part
    if (parts.length >= 3) {
      // Return the 2nd part (after street, before state/country)
      return parts[1];
    }
    if (parts.length === 2) return parts[0];
    return parts[0] || "";
  }

  const handleContinue = async () => {
    if (!selectedBusiness) { toast.error("Please search and select your business."); return; }
    if (!email.trim() || !email.includes("@")) { toast.error("Please enter a valid email address."); return; }
    if (isSubmitting) return;
    setIsSubmitting(true);

    funnelStart("funnel.keywords_loaded");
    funnelTrack("funnel.lead_submitted", {
      place_id: selectedBusiness.place_id,
      email: email.trim().toLowerCase(),
      business_name: selectedBusiness.name,
      step: "1",
    });

    // Save lead
    try {
      await axios.post(`${API}/leads/capture`, {
        email: email.trim().toLowerCase(),
        business_name: selectedBusiness.name,
        city: city.trim() || selectedBusiness.city || selectedBusiness.address?.split(",").slice(-2, -1)[0]?.trim() || "",
        place_id: selectedBusiness.place_id,
        phone_number: phoneNumber.trim(),
        source: "audit_funnel",
      });
    } catch { /* best-effort */ }

    sessionStorage.setItem("audit_lead", JSON.stringify({
      name: "",
      email: email.trim().toLowerCase(),
      phone: phoneNumber.trim(),
      phone_number: phoneNumber.trim(),
      city: city.trim(),
    }));
    setIsSubmitting(false);
    setStep(2);
  };

  const handleGenerateAudit = async (keywords) => {
    if (!selectedBusiness || isSubmitting) return;
    setIsSubmitting(true);
    setGenerating(true);
    setAuditResult(null);
    setPaymentRequired(null);

    funnelStart("funnel.audit_completed");
    funnelTrack("funnel.keywords_selected", {
      place_id: selectedBusiness.place_id,
      email: email.trim().toLowerCase(),
      business_name: selectedBusiness.name,
      step: "2",
      metadata: { keyword_count: keywords?.length || 0 },
    });

    const runAudit = async () => {
      const auditLead = JSON.parse(sessionStorage.getItem("audit_lead") || "{}");
      const res = await axios.post(`${API}/audit`, {
        place_id: selectedBusiness.place_id,
        email: email.trim().toLowerCase(),
        selected_keywords: keywords,
        phone_number: auditLead.phone_number || "",
      });
      return res;
    };

    try {
      const res = await runAudit();
      const placeId = res.data?.place_id || selectedBusiness.place_id;
      sessionStorage.setItem("auditData", JSON.stringify(res.data));
      setAuditResult({ ...res.data, _placeId: placeId });
      funnelTrack("funnel.audit_completed", {
        place_id: placeId,
        email: email.trim().toLowerCase(),
        business_name: selectedBusiness.name,
        step: "3",
      });
    } catch (err) {
      // Handle 402 Payment Required
      if (err.response?.status === 402) {
        setGenerating(false);
        setIsSubmitting(false);
        setPaymentRequired(err.response.data);
        return;
      }
      // Auto-retry once after 3 seconds
      try {
        await new Promise(r => setTimeout(r, 3000));
        const res = await runAudit();
        const placeId = res.data?.place_id || selectedBusiness.place_id;
        sessionStorage.setItem("auditData", JSON.stringify(res.data));
        setAuditResult({ ...res.data, _placeId: placeId });
        funnelTrack("funnel.audit_completed", {
          place_id: placeId,
          email: email.trim().toLowerCase(),
          business_name: selectedBusiness.name,
          step: "3",
          metadata: { retry: true },
        });
      } catch (err2) {
        setGenerating(false);
        setIsSubmitting(false);
        if (err2.response?.status === 402) {
          setPaymentRequired(err2.response.data);
          return;
        }
        const detail = err2.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(", ") : "Audit generation failed. Please try again.";
        toast.error(msg);
        funnelTrack("funnel.audit_failed", {
          place_id: selectedBusiness.place_id,
          email: email.trim().toLowerCase(),
          business_name: selectedBusiness.name,
          step: "3",
          error: msg,
        });
      }
    }
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  /* Auto-fill from landing page form */
  useEffect(() => {
    const raw = sessionStorage.getItem("landing_prefill");
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      sessionStorage.removeItem("landing_prefill");
      if (data.business?.name) {
        setSelectedBusiness(data.business);
        setBusinessQuery(data.business.name);
        setCity(data.city || data.business.city || "");
        setEmail(data.email || "");
        setStep(2);
      }
    } catch { /* ignore */ }
  }, []);

  /* Set page title */
  useEffect(() => {
    if (generating && selectedBusiness?.name) {
      document.title = `Analyzing ${selectedBusiness.name}... | RankifyLocal`;
    } else {
      document.title = "Free Local SEO Audit | RankifyLocal";
    }
    return () => { document.title = "Free Local SEO Audit | RankifyLocal"; };
  }, [generating, selectedBusiness?.name]);

  if (generating) {
    return (
      <LoadingScreen
        businessName={selectedBusiness?.name}
        placeId={selectedBusiness?.place_id}
        auditData={auditResult}
        onComplete={(data) => {
          const placeId = data?._placeId || selectedBusiness?.place_id;
          navigate(`/report/${placeId}`);
        }}
      />
    );
  }

  // Payment required gate
  if (paymentRequired) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex flex-col" data-testid="payment-gate">
        <header className="px-6 py-4">
          <button onClick={() => navigate("/")} className="hover:opacity-80" style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            <Logo size="default" theme="light" />
          </button>
        </header>
        <div className="flex-1 flex items-start justify-center px-4 pt-12 pb-16">
          <Card className="w-full max-w-[520px] bg-white border-[#E5E7EB]" style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
            <CardContent className="p-8 text-center">
              <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-[#EEF2FF] flex items-center justify-center">
                <ShieldCheck className="w-7 h-7 text-[#3B5BDB]" />
              </div>
              <h2 className="text-2xl font-bold text-[#111827] mb-2">You've used your free audit</h2>
              <p className="text-[#6B7280] mb-6">
                Unlock additional audits for <span className="font-bold text-[#111827]">$27 each</span>. Each audit includes:
              </p>
              <div className="text-left space-y-3 mb-8 max-w-xs mx-auto">
                {[
                  { icon: BarChart3, text: "Full 285-point analysis" },
                  { icon: Users, text: "Competitor comparison" },
                  { icon: Zap, text: "Revenue impact calculator" },
                  { icon: FileText, text: "AI visibility check" },
                  { icon: ShieldCheck, text: "Complete fix plan & roadmap" },
                ].map(({ icon: Icon, text }, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Icon className="w-4 h-4 text-[#22c55e] shrink-0" />
                    <span className="text-sm text-[#374151]">{text}</span>
                  </div>
                ))}
              </div>
              <a
                href={paymentRequired.checkout_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full h-12 rounded-xl font-bold text-white text-base transition-all"
                style={{ background: "#3B5BDB" }}
                onMouseOver={e => { e.currentTarget.style.background = "#2F4AC7"; }}
                onMouseOut={e => { e.currentTarget.style.background = "#3B5BDB"; }}
                data-testid="payment-gate-checkout-btn"
              >
                Unlock This Audit — $27 <ArrowRight className="w-4 h-4 ml-2" />
              </a>
              <button
                onClick={() => { setPaymentRequired(null); setIsSubmitting(false); setStep(1); }}
                className="mt-4 text-sm text-[#6B7280] hover:text-[#3B5BDB] transition-colors"
                data-testid="payment-gate-back-btn"
              >
                Go back
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col" data-testid="audit-page">
      {/* Header */}
      <header className="px-6 py-4">
        <button onClick={() => navigate("/")} className="hover:opacity-80" style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          <Logo size="default" theme="light" />
        </button>
      </header>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-3 py-4" data-testid="step-indicator">
        {[
          { num: 1, label: "Your Business" },
          { num: 2, label: "Select Keywords" },
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center gap-3">
            <div className={`flex items-center gap-2 ${step >= s.num ? "text-[#3B5BDB]" : "text-[#9CA3AF]"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.num ? "bg-[#3B5BDB] text-white" : "bg-[#E5E7EB] text-[#9CA3AF]"}`}>{s.num}</div>
              <span className="text-sm font-medium hidden sm:inline">{s.label}</span>
            </div>
            {idx < 1 && <div className={`w-8 sm:w-12 h-px ${step > s.num ? "bg-[#3B5BDB]" : "bg-[#E5E7EB]"}`} />}
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center px-4 pt-8 pb-16">
        <Card className="w-full max-w-[580px] bg-white border-[#E5E7EB]" style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
          <CardContent className="p-8">
            {/* Logo centered above form */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
              <Logo size="large" theme="light" />
            </div>
            <AnimatePresence mode="wait">
              {/* Step 1: Business Search + Email */}
              {step === 1 && (
                <motion.div key="step1" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                  <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#111827] mb-2" data-testid="audit-headline">
                    Enter your email to see your free audit score
                  </h1>
                  <p className="text-[#6B7280] mb-6">
                    We'll analyze your Google listing and show exactly where you rank against local competitors.
                  </p>

                  <div className="space-y-4">
                    {/* Business Search */}
                    <div>
                      <label className="text-xs font-medium text-[#6B7280] mb-1.5 block">Business Name *</label>
                      <div className="relative" data-testid="business-search">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                        {searching && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#3B5BDB] animate-spin" />
                        )}
                        <Input
                          placeholder="Search your business on Google Maps..."
                          value={businessQuery}
                          onChange={e => handleInputChange(e.target.value)}
                          className="pl-10 pr-10 h-12 bg-white border-[#E5E7EB] text-[#111827] text-base"
                          data-testid="business-search-input"
                        />
                        {searching && predictions.length === 0 && businessQuery.length >= 2 && (
                          <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-[#E5E7EB] rounded-lg shadow-lg p-3">
                            <p className="text-xs text-[#9CA3AF] flex items-center gap-2">
                              <Loader2 className="w-3 h-3 animate-spin" /> Searching...
                            </p>
                          </div>
                        )}
                        {predictions.length > 0 && (
                          <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-72 overflow-y-auto" data-testid="predictions-dropdown">
                            {predictions.map((pred, i) => (
                              <button key={i} onClick={() => selectBusiness(pred)}
                                className="w-full flex items-start gap-3 p-3 text-left hover:bg-[#F9FAFB] transition-colors border-b border-[#F3F4F6] last:border-b-0"
                                data-testid={`prediction-${i}`}>
                                <MapPin className="w-4 h-4 text-[#9CA3AF] mt-0.5 shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-[#111827] truncate">{pred.main_text || pred.description}</p>
                                  <p className="text-xs text-[#9CA3AF] truncate">{pred.secondary_text || ""}</p>
                                </div>
                                {pred.rating && (
                                  <span className="text-xs text-[#F59E0B] font-semibold shrink-0 mt-0.5">
                                    {pred.rating} ★
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Selected Business Confirmation */}
                    {selectedBusiness && (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-3 rounded-lg bg-[#ECFDF5] border border-emerald-200">
                        <p className="text-sm font-medium text-[#059669]">{selectedBusiness.name}</p>
                        <p className="text-xs text-[#6B7280]">{selectedBusiness.address}</p>
                      </motion.div>
                    )}

                    {/* City / Location */}
                    <div>
                      <label className="text-xs font-medium text-[#6B7280] mb-1.5 block">City or Postal Code</label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                        <Input
                          value={city}
                          onChange={e => setCity(e.target.value)}
                          placeholder="e.g. Toronto, ON or 90210"
                          className="pl-10 h-12 bg-white border-[#E5E7EB] text-[#111827] text-base"
                          data-testid="city-input"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="text-xs font-medium text-[#6B7280] mb-1.5 block">
                        Your Email Address *
                        {user?.email && <span className="text-emerald-500 ml-2">Logged in</span>}
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                        <Input
                          type="email"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          readOnly={!!user?.email}
                          placeholder="you@business.com"
                          className={`pl-10 h-12 border-[#E5E7EB] text-[#111827] text-base ${user?.email ? "bg-slate-50 cursor-default" : "bg-white"}`}
                          data-testid="lead-email-input"
                        />
                      </div>
                    </div>

                    {/* Phone (optional) */}
                    <div>
                      <label className="text-xs font-medium text-[#6B7280] mb-1.5 block">Phone number (optional)</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                        <Input
                          type="tel"
                          value={phoneNumber}
                          onChange={e => setPhoneNumber(e.target.value)}
                          placeholder="Phone number (optional)"
                          className="pl-10 h-12 bg-white border-[#E5E7EB] text-[#111827] text-base"
                          data-testid="lead-phone-input"
                        />
                      </div>
                      <p className="text-xs text-[#9CA3AF] mt-1 pl-1">Get a free 5-minute consultation call with your results</p>
                    </div>

                    <Button
                      onClick={handleContinue}
                      disabled={!selectedBusiness || !email.trim() || isSubmitting}
                      className="w-full h-12 bg-[#3B5BDB] hover:bg-[#2F4AC7] text-white font-semibold text-base disabled:opacity-50"
                      data-testid="lead-submit-btn"
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ArrowRight className="w-4 h-4 mr-2" />
                      )}
                      {isSubmitting ? "Processing..." : "Run My Free Audit"}
                    </Button>

                    {/* Trust Signals */}
                    <div className="flex flex-col items-center gap-1 pt-1">
                      <p className="text-[10px] text-[#9CA3AF] flex items-center gap-1">
                        <Lock className="w-3 h-3" /> No spam. Your report is private.
                      </p>
                      <p className="text-[10px] text-[#9CA3AF]">
                        Used by 2,800+ local businesses.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Keyword Picker */}
              {step === 2 && selectedBusiness && (
                <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <button onClick={() => setStep(1)} className="text-sm text-[#3B5BDB] hover:underline mb-4 flex items-center gap-1" data-testid="back-to-step1">
                    ← Back
                  </button>
                  <h1 className="text-2xl font-heading font-bold text-[#111827] mb-2">
                    What does your business offer?
                  </h1>
                  <KeywordPicker
                    mode="pre-audit"
                    placeId={selectedBusiness.place_id}
                    businessName={selectedBusiness.name}
                    businessType={selectedBusiness.types?.[0] || selectedBusiness.category || ""}
                    address={selectedBusiness.address || ""}
                    city={city || selectedBusiness.city || ""}
                    onConfirm={handleGenerateAudit}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
